import { useGetElectricBillDetailsListExportQuery } from 'services/electric/queries/useGetElectricBillDetailsListExportQuery';
import { isRequestError } from 'utils/requestError';
import { saveListExcel } from 'utils/tableExport/saveListExcel';
import { Card, InputText, TableListData } from 'components';
import { InputWraper, NumberUtils, useForm, Button } from 'modules';
import React, { ReactInstance, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useParams } from 'react-router-dom';
import {
  useGetElectricBillDetailsQuery,
  useRequestCancelTransactionMutation,
} from 'services';
import type {
  BillPayTransactionEntity,
  PayBillOrderEntity,
} from 'services/openapi';

import {
  FilterType,
  columnsDefined,
  filterOptions,
  getInitFilterValues,
} from './config';
import { PayBillOrderEntityReturn } from 'services/openapi';
import {
  ModalConfirmElement,
  SelectedA4Voices,
  SelectedThernalVoices,
} from 'features/transactions/Components';
import { useReactToPrint } from 'react-to-print';

export const formData = (data: PayBillOrderEntity) => {
  return {
    orderCode: data?.orderCode,
    txSum: data?.txSum,
    txInProcessSum: data?.txInProcessSum,
    txSuccessSum: data?.txSuccessSum,
    orderStatus: data?.orderStatus,
  };
};

const PayBillElectricDetailsFeature = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);

  const printA4Ref = useRef<ReactInstance>(null);

  const printThernalRef = useRef<ReactInstance>(null);

  const { orderCode } = useParams();

  const [variables, setVariables] = useState<FilterType>({
    ...initFilterValues,
    orderCode,
  });

  const { mutateAsync: mutateRequestCancelTransactionAsync } =
    useRequestCancelTransactionMutation();

  // const { mutateAsync: mutateApproveCancelTransactionAsync } = useRequestCancelTransactionMutation();

  const [data, setData] = useState<PayBillOrderEntityReturn>();

  const [transactionCode, setTransactionCode] = useState<string>('');

  const [isOpen, setIsOpen] = useState<boolean>(!!transactionCode);

  // const [dataSelected, setDataSelected] = useState<TransPayEntity[]>([]);
  const [dataSelected, setDataSelected] = useState<BillPayTransactionEntity[]>(
    [],
  );

  useEffect(() => {
    setIsOpen(!!transactionCode);
  }, [transactionCode]);

  const handleRequestCancelTransaction = async (transactionCode: string) => {
    try {
      await mutateRequestCancelTransactionAsync({ transactionCode });

      toast.success('Yêu cầu hủy giao dịch thành công');
    } catch (_) {}
  };

  // const handleApproveCancelTransaction = async (transactionCode: string) => {
  //   try {
  //     await mutateApproveCancelTransactionAsync({ transactionCode });
  //     toast.success("Hủy giao dịch thành công");
  //   } catch (error) {
  //     toast.error("Hủy giao dịch không thành công");
  //     console.log(error);
  //   }
  // };

  const handleOnClickCancelTransaction = async () => {
    setIsOpen(false);

    await handleRequestCancelTransaction(transactionCode);
  };

  const elementCancelTransaction = () => {
    return (
      <p>
        Xác nhận hủy giao dịch{' '}
        <span style={{ color: 'red' }}>{String(transactionCode)}</span> ?
      </p>
    );
  };

  const titleCancelTransaction = () => {
    return 'Gửi yêu cầu hủy';
  };

  const labelApproveCancelTransaction = () => {
    return 'Gửi yêu cầu hủy';
  };

  const { getInputProps } = useForm({
    structure: filterOptions(data),
    onSubmit: () => {},
  });

  const handlePrintWebA4Invoice = useReactToPrint({
    content: () => printA4Ref?.current,
    copyStyles: true,
    removeAfterPrint: true,
    pageStyle: `
    @page {
        size: A4;
        margin: 40pt 15pt;
    }
    `,
    documentTitle: '',
    suppressErrors: true,
  });

  const handlePrintWebThernalInvoice = useReactToPrint({
    content: () => printThernalRef?.current,
    copyStyles: true,
    removeAfterPrint: true,
    pageStyle: `
    @page {
        size: 225px 690px;
    }
    @page {
      size: auto;
    }
    .print-thermal-content {
      width: 225px;
      height: 690px;
      page-break-after: always;
      margin-top: 0;
      margin-right: 1rem;
      margin-bottom: 0;
      margin-left: 0;
      min-height: auto;
      content: ".";
    }
    `,
    documentTitle: '',
    suppressErrors: true,
  });

  const { isFetching: isLoadingExport, refetch: refetchExport } =
    useGetElectricBillDetailsListExportQuery({
      ...variables,
      orderCode: variables.orderCode || '',
      page: '1',
      pageSize: String(
        Number(
          (data as unknown as { total?: number })?.total ??
            (data?.data as unknown as { total?: number })?.total ??
            0,
        ),
      ),
    });

  const handleExportExcel = async () => {
    if (isLoadingExport) return;
    const total =
      (data as unknown as { total?: number })?.total ??
      (data?.data as unknown as { total?: number })?.total;
    if (total == null) {
      toast.error('Response thiếu total, không thể xuất đầy đủ danh sách');
      return;
    }
    try {
      const result = await refetchExport({ throwOnError: true });
      const summary = result.data as any;
      await saveListExcel(
        result.data?.data?.billTransactions,
        columnsDefined(setTransactionCode),
        'Danh_sach_giao_dich_hoa_don_dien',
        true,
        {
          totals: {
            period: 'TỔNG',
            amount: NumberUtils.toFormatNumber(
              Number(summary.data?.sumAmount ?? 0),
            ),
            paymentAmount: NumberUtils.toFormatNumber(
              Number(summary.data?.sumPayAmount ?? 0),
            ),
            rollbackAmount: NumberUtils.toFormatNumber(
              Number(summary.data?.sumRollBackAmount ?? 0),
            ),
          },
        },
      );
    } catch (error) {
      if (!isRequestError(error)) {
        toast.error(
          error instanceof Error ? error.message : 'Không thể tải danh sách',
        );
      }
    }
  };

  return (
    <>
      <Card className='container-fluid electric-bill-access mb30'>
        <form className='row'>
          <div className='col-lg-4 col-12'>
            <InputWraper
              inputProps={getInputProps('orderCode')}
              renderInput={(props) => (
                <InputText
                  {...props}
                  value={data?.data?.orderCode?.toString()}
                />
              )}
            />
          </div>

          <div className='col-lg-4 col-12'>
            <InputWraper
              inputProps={getInputProps('txSum')}
              renderInput={(props) => (
                <InputText {...props} value={data?.data?.txSum?.toString()} />
              )}
            />
          </div>

          <div className='col-lg-4 col-12'>
            <InputWraper
              inputProps={getInputProps('txSuccessSum')}
              renderInput={(props) => (
                <InputText
                  {...props}
                  value={data?.data?.txSuccessSum?.toString()}
                />
              )}
            />
          </div>

          <div className='col-lg-4 col-12'>
            <InputWraper
              inputProps={getInputProps('txFailedSum')}
              renderInput={(props) => (
                <InputText
                  {...props}
                  value={data?.data?.txFailedSum?.toString()}
                />
              )}
            />
          </div>

          <div className='col-lg-4 col-12'>
            <InputWraper
              inputProps={getInputProps('orderStatus')}
              renderInput={(props) => (
                <InputText
                  {...props}
                  value={data?.data?.orderStatus?.toString()}
                />
              )}
            />
          </div>
          <div className='textCenter mt16 mb20 wrapButtonFilter'>
            <Button
              className='ml15'
              onClick={handleExportExcel}
              label={'Tải về DS'}
              type='button'
              buttonType='info'
              isLoading={isLoadingExport}
            />
          </div>
        </form>
      </Card>

      <TableListData<BillPayTransactionEntity>
        columns={columnsDefined(setTransactionCode)}
        useQuery={useGetElectricBillDetailsQuery}
        variables={variables}
        setVariables={setVariables}
        dataQueryKey='billTransactions'
        filterKey='searchDetailContent'
        callBackData={setData}
        onSelectedRowsCallback={setDataSelected}
        canSelectable={(data) => data?.status === 'Thành công'}
        selectable
        orderColumn
        customHeaderBtnGroup={() => (
          // <ReactToPrint
          //   trigger={() => <Button label="Chọn in hóa đơn" onClick={handlePrintWebA4Invoice} disabled={!dataSelected?.length} type="button" buttonType="success-outline" className="ml5 mr5" />}
          //   content={() => printA4Ref?.current}
          // />
          <div className='row justify-content-end align-items-center ml5'>
            <Button
              label='In A4'
              onClick={handlePrintWebA4Invoice}
              disabled={!dataSelected?.length}
              type='button'
              buttonType='success-outline'
              className='ml5 mr5'
            />
            <Button
              label='In Nhiệt'
              onClick={handlePrintWebThernalInvoice}
              disabled={!dataSelected?.length}
              type='button'
              buttonType='success-outline'
              className='ml5 mr5'
            />
          </div>
        )}
        customTopRow={(data: any) => (
          <tr>
            <td colSpan={7}></td>
            <td>
              <b>TỔNG</b>
            </td>
            <td>
              <b>{NumberUtils.toFormatNumber(Number(data?.data?.sumAmount))}</b>
            </td>
            <td>
              <b>
                {NumberUtils.toFormatNumber(Number(data?.data?.sumPayAmount))}
              </b>
            </td>
            <td>
              <b>
                {NumberUtils.toFormatNumber(
                  Number(data?.data?.sumRollBackAmount),
                )}
              </b>
            </td>
          </tr>
        )}
      />

      <div style={{ display: 'none' }}>
        <SelectedA4Voices
          ref={printA4Ref}
          selectedInvoices={dataSelected}
          screenOrder={true}
        />
        <SelectedThernalVoices
          ref={printThernalRef}
          selectedInvoices={dataSelected}
          screenOrder={true}
        />
      </div>

      <ModalConfirmElement
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSuccess={handleOnClickCancelTransaction}
        title={titleCancelTransaction()}
        labelApprove={labelApproveCancelTransaction()}
      >
        {elementCancelTransaction()}
      </ModalConfirmElement>
    </>

    // <ModalConfirm
    //   isOpen={isOpen}
    //   onSuccess={handleOnClickCancelTransaction}
    //   onClose={() => {
    //     setIsOpen(false);
    //     setTransactionCode("");
    //   }}
    // />
  );
};

export default PayBillElectricDetailsFeature;
